import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
  Box,
} from "@mui/material";
import SoftButton from "components/SoftButton";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
// import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import swal from "sweetalert2";
import { api } from "utils/Api";
import { useHierarchy } from "context/HierarchyContext";
import { showSuccess } from "utils/messageAlerts";

function PropertiesPopupForm({ onClose, mode, property, refreshProperties }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    property_code: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pin_code: "",
    active: true,
    manager_email: ""
  });
  const { orgId } = useHierarchy();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Prefill data when editing
  useEffect(() => {
    if (mode === "edit" && property) {
      setForm({
        name: property.name || "",
        description: property.description || "",
        property_code: property.property_code || "",
        address: property.address || "",
        city: property.city || "",
        state: property.state || "",
        country: property.country || "",
        pin_code: property.pin_code?.toString() || "",
        active: property.active ?? true,
      });
    }
  }, [mode, property]);

  // Generic change handler
  const handleChange = (field, value) => {
    let newValue = value;
    if (field === "property_code") { newValue = value.toUpperCase(); }
    if (field === "pin_code") { newValue = value.toUpperCase().replace(/[^A-Z0-9 ]/g, ""); }
    setForm((prev) => ({ ...prev, [field]: newValue }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Validation
  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Required";
    if (!form.property_code.trim()) newErrors.property_code = "Required";
    if (!form.address.trim()) newErrors.address = "Required";
    if (!form.city.trim()) newErrors.city = "Required";
    if (!form.state.trim()) newErrors.state = "Required";
    if (!form.country.trim()) newErrors.country = "Required";
    if (!form.pin_code.trim()) newErrors.pin_code = "Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  const code = form.property_code.trim().toUpperCase();
  
  const propertyCodeRegex = /^(?!.*-.*-)[A-Z0-9-]/;

  if (!propertyCodeRegex.test(code)) {
    showWarning("Property code can only contain uppercase letters, numbers, and a single hyphen");
    return;
  }

  setLoading(true);

  const payload = {
    ...form,
    name: form.name.trim(),
    description: form.description.trim(),
    property_code: code, // UPPERCASE sent
    address: form.address.trim(),
    city: form.city.trim(),
    state: form.state.trim(),
    country: form.country.trim(),
    pin_code: form.pin_code.trim(),
    organization_id: Number(orgId),
  };

  try {
    setLoading(true);

    let propertyId;

    // 1️⃣ Create or Update Property
    if (mode === "create") {
      const response = await api.su_Property.createSuProperty(payload);
      propertyId = response.id; // adjust if response.data.id
      showSuccess("Property created successfully");
    } else {
      await api.su_Property.updateSuPropertyDetails(property.id, payload);
      propertyId = property.id;
      showSuccess("Property updated successfully");
    }
    refreshProperties(); // Refresh list after create/update
    onClose();

  } catch (err) {
    
  } finally {
    setLoading(false);
  }
};

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {mode === "create" ? "Create Property" : "Edit Property"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Field label="Property Name" value={form.name} error={errors.name}
              onChange={(v) => handleChange("name", v)} required />

            <Field label="Property Code(EX. ABC-12, abc-123, ABC123, ABC-1A)" value={form.property_code} error={errors.property_code} inputProps={{ maxLength: 6 }}
              onChange={(v) => handleChange("property_code", v)} required />

            <Field label="Description" value={form.description}
              onChange={(v) => handleChange("description", v)} fullWidth />

            <Field label="Address" value={form.address} error={errors.address} required
              onChange={(v) => handleChange("address", v)} fullWidth  />

            <Field label="City" value={form.city} error={errors.city} required
              onChange={(v) => handleChange("city", v)}  />

            <Field label="State" value={form.state} error={errors.state} required
              onChange={(v) => handleChange("state", v)} />

            <Field label="Country" value={form.country} error={errors.country} required
              onChange={(v) => handleChange("country", v)} />

            <Field label="PIN Code" type="text" value={form.pin_code} error={errors.pin_code} required
              onChange={(v) => handleChange("pin_code", v)} inputProps={{ maxLength: 10 }} />
            {/* {mode === "create" && (
              <Field
                placeholder={`Enter user email to assign as manager`}
                label="Manager Email"
                value={form.manager_email}
                error={errors.manager_email}
                onChange={(v) => handleChange("manager_email", v)}
                fullWidth
                required
            />
            )} */}
          </Grid>
        </DialogContent>

        <DialogActions>
          <SoftButton onClick={onClose}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="dark" disabled={loading} type="submit">
            {loading ? "Saving..." : "Save"}
          </SoftButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/* Reusable Responsive Field */
function Field({ label, value, onChange, inputProps, error, required, type = "text", fullWidth }) {
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <SoftTypography variant="caption" fontWeight="bold">
        {label} {required && (<Box component="span" color="error.main"> *</Box> )}
      </SoftTypography>
      <SoftInput
        type={type}
        placeholder={`Enter ${label}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        inputProps={inputProps}   // <-- forward it
      />
    </Grid>
  );
}

Field.propTypes = {
label: PropTypes.string.isRequired,
value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
onChange: PropTypes.func.isRequired,
error: PropTypes.string,
required: PropTypes.bool,
type: PropTypes.string,
fullWidth: PropTypes.bool,
inputProps: PropTypes.object,
};

PropertiesPopupForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(["create", "edit"]).isRequired,
    property: PropTypes.object,
    refreshProperties: PropTypes.func.isRequired,
};

export default PropertiesPopupForm;